<script setup lang="ts">
import {computed, onBeforeUnmount, onMounted, ref, shallowRef, watch} from "vue";
import type {ICellRendererParams} from "ag-grid-community";
import {echarts} from "@/utils/common/echartsSetup.ts";

type BarOptions = { min?: number; max?: number; color?: string; showValue?: boolean };
type ProgressOptions = { min?: number; max?: number; showLabel?: boolean };

const props = defineProps<{
  params: ICellRendererParams & {
    rendererOptions?: { bar?: BarOptions; progress?: ProgressOptions };
    colId?: string;
    /** 'bar' or 'progress' — set by buildColDef so one component handles both */
    mode?: 'bar' | 'progress';
  };
}>();

const container = ref<HTMLDivElement | null>(null);
const chart = shallowRef<echarts.ECharts | null>(null);
let resizeObserver: ResizeObserver | null = null;

function resolveColId(): string | undefined {
  return props.params.colId ?? props.params.column?.getColId();
}

const numericValue = computed<number>(() => {
  const data: any = props.params.data;
  const colId = resolveColId();
  const raw = data?.cells?.[colId ?? '']?.value;
  const n = typeof raw === 'number' ? raw : parseFloat(String(raw));
  return isNaN(n) ? 0 : n;
});

const cfg = computed(() => {
  const mode = props.params.mode ?? 'bar';
  if (mode === 'progress') {
    const p = props.params.rendererOptions?.progress ?? {};
    return {min: p.min ?? 0, max: p.max ?? 100, color: '#2196f3'};
  }
  const b = props.params.rendererOptions?.bar ?? {};
  return {min: b.min ?? 0, max: b.max ?? 100, color: b.color ?? '#4caf50'};
});

function buildOption() {
  return {
    animation: false,
    grid: {top: 4, right: 4, bottom: 4, left: 4, containLabel: false},
    xAxis: {type: 'value', show: false, min: cfg.value.min, max: cfg.value.max},
    yAxis: {type: 'category', show: false, data: ['v']},
    tooltip: {trigger: 'item', confine: true, formatter: () => String(numericValue.value)},
    series: [{
      type: 'bar',
      data: [numericValue.value],
      barWidth: '70%',
      itemStyle: {color: cfg.value.color, borderRadius: 2},
    }],
  };
}

function ensureChart() {
  if (!container.value) return;
  const w = container.value.clientWidth;
  const h = container.value.clientHeight;
  if (w === 0 || h === 0) {
    requestAnimationFrame(ensureChart);
    return;
  }
  if (!chart.value) chart.value = echarts.init(container.value, undefined, {renderer: 'canvas'});
  chart.value.setOption(buildOption(), true);
}

onMounted(() => {
  requestAnimationFrame(ensureChart);
  if (container.value && 'ResizeObserver' in window) {
    resizeObserver = new ResizeObserver(() => chart.value?.resize());
    resizeObserver.observe(container.value);
  }
});

watch(numericValue, () => ensureChart());
watch(cfg, () => ensureChart(), {deep: true});

onBeforeUnmount(() => {
  resizeObserver?.disconnect();
  resizeObserver = null;
  chart.value?.dispose();
  chart.value = null;
});
</script>

<template>
  <div ref="container" style="width: 100%; height: 28px; display: block;"></div>
</template>
